import { AlertTriangle, ArrowLeft, Loader2 } from "lucide-react";
import { Link, useParams } from "react-router";

import { BrandArrow } from "@/components/brand-arrow";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  useMe,
  usePublishStatus,
  useSubmitStatus,
  useTrack,
  useTrackStatuses,
  useUnpublishStatus,
  type WeeklyStatus,
} from "@/lib/api";
import { ragBadgeClass, ragLabel } from "@/lib/rag";
import { cn } from "@/lib/utils";

import { StatusEditor } from "./status-editor";
import { StatusTimeline } from "./status-timeline";

function formatDate(value: string | null | undefined): string {
  if (!value) {
    return "—";
  }
  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function TrackDetailPage() {
  const { id } = useParams<{ id: string }>();
  const trackId = id ?? null;

  const { data: me } = useMe();
  const trackQuery = useTrack(trackId);
  const statusesQuery = useTrackStatuses(trackId);
  const submitMutation = useSubmitStatus();
  const publishMutation = usePublishStatus();
  const unpublishMutation = useUnpublishStatus();

  const track = trackQuery.data;
  const statuses = statusesQuery.data ?? [];

  const isAdmin = me?.role === "admin";
  const isOwner = !!me?.id && !!track?.owner_id && me.id === track.owner_id;
  const canEdit = isAdmin || isOwner;

  const latestRag =
    [...statuses].sort(
      (a, b) => new Date(b.week_start).getTime() - new Date(a.week_start).getTime(),
    )[0]?.rag ?? null;

  const draft = statuses.find((s) => s.state === "draft") ?? null;

  const renderActions = (status: WeeklyStatus) => {
    if (!trackId) {
      return null;
    }
    const nodes = [];
    if (status.state === "draft" && canEdit) {
      nodes.push(
        <Button
          key="submit"
          size="sm"
          disabled={submitMutation.isPending}
          onClick={() => void submitMutation.mutateAsync({ statusId: status.id, trackId })}
        >
          Submit for review
          <BrandArrow />
        </Button>,
      );
    }
    if (status.state === "in_review" && isAdmin) {
      nodes.push(
        <Button
          key="publish"
          size="sm"
          disabled={publishMutation.isPending}
          onClick={() => void publishMutation.mutateAsync({ statusId: status.id, trackId })}
        >
          Publish
          <BrandArrow />
        </Button>,
      );
    }
    if (status.state === "published" && isAdmin) {
      nodes.push(
        <Button
          key="unpublish"
          size="sm"
          variant="outline"
          disabled={unpublishMutation.isPending}
          onClick={() => void unpublishMutation.mutateAsync({ statusId: status.id, trackId })}
        >
          Unpublish
        </Button>,
      );
    }
    return nodes.length > 0 ? <>{nodes}</> : null;
  };

  return (
    <div className="bg-background min-h-screen">
      <header className="border-border flex items-center justify-between border-b px-6 py-4">
        <div className="flex items-center gap-3">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <h1 className="text-foreground text-xl">{track?.name ?? "Track detail"}</h1>
        </div>
        <Link to="/home">
          <Button variant="outline">Home</Button>
        </Link>
      </header>

      <main className="mx-auto max-w-4xl space-y-6 px-6 py-8">
        {trackQuery.isLoading ? (
          <Card>
            <CardContent className="flex justify-center py-16">
              <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
            </CardContent>
          </Card>
        ) : trackQuery.isError || !track ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
              <AlertTriangle className="text-destructive h-6 w-6" />
              <p className="text-muted-foreground text-sm">Could not load this track.</p>
              <Button variant="outline" onClick={() => void trackQuery.refetch()}>
                Try again
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardContent className="space-y-3 p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="text-foreground text-2xl">{track.name}</h2>
                    {track.business_unit && (
                      <p className="text-muted-foreground text-sm">{track.business_unit}</p>
                    )}
                  </div>
                  {latestRag && (
                    <span className={cn(ragBadgeClass(latestRag))}>{ragLabel(latestRag)}</span>
                  )}
                </div>
                {track.description && (
                  <p className="text-muted-foreground text-sm">{track.description}</p>
                )}
                <div className="text-muted-foreground flex flex-wrap gap-x-6 gap-y-1 text-sm">
                  <span>Owner: {track.owner_name ?? "Unassigned"}</span>
                  <span>Target: {formatDate(track.target_date)}</span>
                </div>
              </CardContent>
            </Card>

            {canEdit && trackId && <StatusEditor trackId={trackId} draft={draft} />}

            <div>
              <h3 className="text-foreground mb-3 text-lg">Status history</h3>
              {statusesQuery.isLoading ? (
                <Card>
                  <CardContent className="flex justify-center py-10">
                    <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
                  </CardContent>
                </Card>
              ) : (
                <StatusTimeline statuses={statuses} renderActions={renderActions} />
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
